// @ts-nocheck
// Company list → Quick Access "View" modal. One modal with four tabs
// (Call Strategies / Dialer Groups / Locations / Country Codes) replacing
// the four separate per-icon modals. All four associations are fetched in
// parallel when the modal opens; each tab shows its own loading/error state.
import React, { useEffect, useState } from 'react';
import { Target, Users, MapPin, Flag, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { friendlyApiError } from '@/utilities/apiErrorMessage';

// Same pill styling as the page's My Companies / All / Demo tabs. Tab
// colors match the old Quick Access icon buttons (user-forms.css).
const TABS_LIST_CLS =
  'h-auto w-full flex-nowrap gap-1 overflow-x-auto rounded-full border border-border/60 bg-muted/40 p-1 shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)]';
const TAB_TRIGGER_CLS =
  'flex-1 gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground data-[state=active]:bg-primary/15 data-[state=active]:!text-primary data-[state=active]:font-semibold data-[state=active]:shadow-[2px_2px_5px_rgba(163,177,198,0.55),-2px_-2px_5px_rgba(255,255,255,0.9)]';

const TABS = [
  {
    key: 'strategies',
    color: '#1e40af',
    label: 'Call Strategies',
    icon: Target,
    empty: 'No strategies assigned.',
    errorMsg: 'Failed to load call strategies',
    rowsKey: 'companyCallStrategies',
    pick: (rows) => rows.filter((x) => x.strategyMaster && !x.is_deleted),
    render: (x) => ({ title: x.strategyMaster?.name }),
  },
  {
    key: 'groups',
    color: '#7c3aed',
    label: 'Dialer Groups',
    icon: Users,
    empty: 'No dialer groups assigned.',
    errorMsg: 'Failed to load dialer groups',
    rowsKey: 'companyGroups',
    pick: (rows) => rows.filter((g) => g.groups && !g.groups.is_deleted),
    render: (g) => ({ title: g.groups?.group_name }),
  },
  {
    key: 'locations',
    color: '#059669',
    label: 'Locations',
    icon: MapPin,
    empty: 'No locations assigned.',
    errorMsg: 'Failed to load locations',
    rowsKey: 'companyLocations',
    pick: (rows) => rows.filter((l) => l.locations && !l.locations.is_deleted),
    render: (l) => ({ title: l.locations?.location_name }),
  },
  {
    key: 'directions',
    color: '#d97706',
    label: 'Country Codes',
    icon: Flag,
    empty: 'No country codes assigned.',
    errorMsg: 'Failed to load country codes',
    rowsKey: 'companyCountryCodeDirections',
    pick: (rows) => rows.filter((r) => r.directionMaster && !r.is_deleted),
    render: (d) => {
      const country = d.directionMaster?.country_code || {};
      return {
        title: country?.name || 'Unknown',
        code: country?.calling_code ? `+${country.calling_code}` : '',
        enabled: d.directionMaster?.is_enabled !== false,
      };
    },
  },
];

const EMPTY_STATE = { loading: false, error: false, rows: [] };

const CompanyQuickAccessModal = ({ company, onClose, fetchAssociations, onLoaded }) => {
  const [activeTab, setActiveTab] = useState('strategies');
  const [data, setData] = useState({});

  useEffect(() => {
    if (!company) return;
    let cancelled = false;
    setActiveTab('strategies');
    setData(Object.fromEntries(TABS.map((t) => [t.key, { ...EMPTY_STATE, loading: true }])));
    TABS.forEach((tab) => {
      fetchAssociations(company.id, tab.key)
        .then((resp) => {
          if (cancelled) return;
          const rows = resp?.[tab.rowsKey] || [];
          onLoaded?.(company.id, { [tab.rowsKey]: rows });
          setData((prev) => ({ ...prev, [tab.key]: { loading: false, error: false, rows: tab.pick(rows) } }));
        })
        .catch((err) => {
          if (cancelled) return;
          toast.error(friendlyApiError(err) || tab.errorMsg);
          setData((prev) => ({ ...prev, [tab.key]: { loading: false, error: true, rows: [] } }));
        });
    });
    return () => { cancelled = true; };
  }, [company?.id]);

  return (
    <Dialog open={!!company} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Quick Access</DialogTitle>
        </DialogHeader>
        <div className="text-sm">
          <span className="text-muted-foreground">Company Name: </span>
          <span className="font-semibold text-foreground">{company?.name || '—'}</span>
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={TABS_LIST_CLS}>
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const state = data[tab.key] || EMPTY_STATE;
              return (
                <TabsTrigger key={tab.key} value={tab.key} className={TAB_TRIGGER_CLS}>
                  <Icon className="h-3.5 w-3.5" style={{ color: tab.color }} />
                  {tab.label}
                  {!state.loading && !state.error && ` (${state.rows.length})`}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {TABS.map((tab) => {
            const state = data[tab.key] || EMPTY_STATE;
            return (
              <TabsContent key={tab.key} value={tab.key} className="mt-4">
                {state.loading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Loading {tab.label.toLowerCase()}…</div>
                ) : state.error ? (
                  <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                    Couldn't load {tab.label.toLowerCase()}.
                  </div>
                ) : state.rows.length === 0 ? (
                  <div className="rounded-md border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
                    {tab.empty}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {state.rows.map((row, idx) => {
                      const item = tab.render(row);
                      const Icon = tab.key === 'directions' ? Globe : tab.icon;
                      return (
                        <div
                          key={idx}
                          className="flex items-center gap-3 rounded-md border border-border/60 bg-card px-3 py-2 text-sm"
                        >
                          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
                            <Icon className="h-4 w-4" style={{ color: tab.color }} />
                          </span>
                          <span className="font-medium text-foreground">{item.title || '—'}</span>
                          {item.code && (
                            <span className="rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
                              {item.code}
                            </span>
                          )}
                          {item.enabled !== undefined && (
                            <span
                              className={
                                'ml-auto inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
                                (item.enabled
                                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                  : 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300')
                              }
                            >
                              {item.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            );
          })}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default CompanyQuickAccessModal;
